import React, { useEffect, useState } from 'react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { BenefitStrip } from '../components/BenefitStrip';
import { ProblemSection } from '../components/ProblemSection';
import { LensSection } from '../components/LensSection';
import { UseCaseSection } from '../components/UseCaseSection';
import { StorySection } from '../components/StorySection';
import { DemoSection } from '../components/DemoSection';
import { DetailSection } from '../components/DetailSection';
import { SpecsSection } from '../components/SpecsSection';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AssuranceSection } from '../components/AssuranceSection';
import { ValueStackSection } from '../components/ValueStackSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { FaqSection } from '../components/FaqSection';
import { FinalCta } from '../components/FinalCta';
import { DiscountSection } from '../components/DiscountSection';
import { SiteFooter } from '../components/SiteFooter';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { ObjectionRecovery } from '../components/MiniFunnels';
import { ObjectionModal } from '../components/ObjectionModal';
import { useObjectionTrigger } from '../hooks/useObjectionTrigger';
import { trackCustom, packEvents } from '../utils/tracking';
import { onOfferRequest } from '../utils/offerBus';
import { Offer } from '../data/product';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
type OfferId = 'single' | 'trio' | 'crew';
interface HomeProps {
  showAnnouncementBar?: boolean;
  defaultOffer?: OfferId;
}
export function Home({
  showAnnouncementBar = true,
  defaultOffer = 'trio'
}: HomeProps) {
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(defaultOffer);
  const [activeObjection, setActiveObjection] = useState<ObjectionId | null>(null);
  const [priceUnlocked, setPriceUnlocked] = useState(false);
  const objection = useObjectionTrigger();
  const handleSelectOffer = (id: Offer['id']) => {
    setSelectedOffer(id);
    trackCustom(packEvents[id].selected);
  };

  // CTAs lower on the page preselect the pack they argue for.
  useEffect(() => onOfferRequest(id => setSelectedOffer(id)), []);
  const handleObjection = (id: ObjectionId) => {
    const choice = objectionChoices.find(item => item.id === id);
    if (!choice) return;
    setActiveObjection(id);
    trackCustom(choice.event);
    // The 20% single-unit rescue only exists inside the price path.
    if (id === 'price') setPriceUnlocked(true);
    setSelectedOffer(choice.recommend);
    objection.close();
    // Wait for the revealed funnel to mount before scrolling to it.
    window.setTimeout(() => {
      document.getElementById(RECOVERY_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };

  // "None of these. I want it." Take the 5% code and go straight to the packs.
  const handleWantIt = () => {
    trackCustom('objection_none_want_it');
    trackCustom('rescue_5_clicked');
    objection.close();
    window.setTimeout(() => {
      document.getElementById('offer')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1091" data-mp="kewm2knn81lc">
      {showAnnouncementBar && <AnnouncementBar data-id="element-1092" data-mp="3su6vyexryb3o" />}
      <SiteNav data-id="element-1093" data-mp="3ntixx9b4otzy" />
      <main className="pb-24 lg:pb-0" data-id="element-1094" data-mp="3nu12ct7y2eiy">
        {/* 1. Promise + product */}
        <Hero data-id="element-1095" data-mp="5necb4ldn3af" />
        {/* 2. Instant reassurance */}
        <BenefitStrip data-id="element-1096" data-mp="5oxrssz7lab5" />
        {/* 3. Early credibility — 50,000+ users, 4.9/5 */}
        <SocialProofSection data-id="element-1097" data-mp="nrzgw9gnp25" />
        {/* 4. Pain + head-to-head against the old way */}
        <ProblemSection data-id="element-1098" data-mp="1h1rwnzgx90lp" />
        {/* 5. The transformation, made visual */}
        <DemoSection data-id="element-1099" data-mp="1c486c16y8xde" />
        {/* 6. The differentiator: 3 lenses */}
        <LensSection data-id="element-1100" data-mp="1c62ox8ur0deb" />
        {/* 7. Rational justification */}
        <DetailSection data-id="element-1101" data-mp="27fg0oe1f7lz4" />
        {/* 7b. The questions people ask before buying */}
        <SpecsSection data-id="element-1102" data-mp="2ce11ofohy9om" />
        {/* 8. Self-identification: "that's my job" */}
        <UseCaseSection data-id="element-1103" data-mp="2hbjbe8ac8sxo" />
        {/* 8b. Who is behind the gear */}
        <StorySection data-id="element-1104" data-mp="2hef9pxq3ljmn" />
        {/* 9. Risk removed before any price is shown */}
        <GuaranteeSection data-id="element-1105" data-mp="2m92r7cmopdrq" />
        {/* 9b. Shipping, returns and risk */}
        <AssuranceSection data-id="element-1106" data-mp="2qbzg1l2ti644" />
        {/* 10. The one economic comparison: what it all costs separately */}
        <ValueStackSection data-id="element-1107" data-mp="2va9d6183rgsd" />
        {/* 11. The decision */}
        <OfferSection selectedOffer={selectedOffer} onSelectOffer={handleSelectOffer} data-id="element-1108" data-mp="3020urm9a6c6d" />
        {/* 12. Personalized recovery area: hidden until the visitor tells us what is holding them back */}
        <ObjectionRecovery active={activeObjection} priceUnlocked={priceUnlocked} onChooseAnother={objection.reopen} data-id="element-1109" data-mp="3a19tesf3cez2" />
        {/* 13. Last objections */}
        <FaqSection data-id="element-1110" data-mp="12eq5kdjqcbq8" />
        {/* 14. Close */}
        <FinalCta onSelectOffer={handleSelectOffer} data-id="element-1111" data-mp="12gjg43xgrg3i" />
        {/* 15. Last-chance code for anyone still on the fence */}
        <DiscountSection data-id="element-1112" data-mp="yfhczc0fejw9" />
      </main>
      <SiteFooter data-id="element-1113" data-mp="tds5258i2mce" />
      <StickyBuyBar selectedOffer={selectedOffer} data-id="element-1114" data-mp="tf35pw6nfdza" />
      <ObjectionModal open={objection.open} onClose={objection.close} onChoose={handleObjection} onWantIt={handleWantIt} data-id="element-1115" data-mp="of723y1v5ok9" />
    </div>;
}